import { createContext, Suspense, use, useCallback, useEffect, useMemo, useRef } from 'react';
import { type FieldValues, FormProvider, useForm } from 'react-hook-form';

import { Skeleton } from 'antd';

import { STEP_VALIDATORS, type StepName as DomainStepName } from '@eduzz-registry-360/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import { message } from '@eduzz/ui-antd-theme';

import { FlowContext } from '@/components/Flow';
import { type GetErrorInfo, type Loading } from '@/hooks/useLoading';
import { useLoadingCleaner } from '@/hooks/useLoadingCleaner';
import { useSaveStep } from '@/hooks/useSaveStep';
import { useZustandSync } from '@/hooks/useZustandSync';
import { trackFieldErrors, trackStepComplete } from '@/lib/gtm';
import useRegistryStore, { registryStore } from '@/stores/registry';

import { Content } from './Content';
import { Footer } from './Footer';
import { defaultValuesForValidator, extractShape, scrollToFirstFieldError } from './formDefaults';
import { Label } from './Label';
import { allSteps } from './registry';
import { StepScopeProvider } from './scope';
import { Subtitle } from './Subtitle';
import { Title } from './Title';
import { type Step as StepType } from '../Flow/types';
import { WizardContext } from '../Wizard';
import { FlowListContext } from '../Wizard/FlowList';

type LoadingOptions<E = unknown> = Omit<Loading, 'lastFlow' | 'action' | 'getErrorInfo'> & {
  error: GetErrorInfo<E>;
};

export const StepContext = createContext<{
  next: <E = unknown>(action?: () => Promise<unknown>, loading?: LoadingOptions<E>) => void;
  previous: () => void;
}>({
  next: () => {},
  previous: () => {}
});

export type StepProps = StepType;

const isBackendStep = (name: string): name is DomainStepName => name in STEP_VALIDATORS;

export const Step = ({ name, untraceable }: StepProps) => {
  const { triggerLoading, currentFlowList, flowlistHistory, pendingFieldErrors, clearPendingFieldErrors } =
    use(WizardContext);
  const { name: flowListName } = use(FlowListContext);
  const { nextStep, previousStep, currentStep: flowCurrentStep } = use(FlowContext);
  const scopePrefix = `${flowListName ?? 'flow'}-${name}`;
  const saveStep = useSaveStep();
  const nextLocked = useRef(false);
  const previousFlowListRef = useRef(currentFlowList);
  const { bankAccountDocument, personType, legalEntityRelation } = useRegistryStore(({ registry }) => ({
    bankAccountDocument: registry.bankAccountDocument,
    personType: registry.personType,
    legalEntityRelation: registry.legalEntityRelation
  }));

  const currentStep = allSteps[name];
  const resolvedValidator = (currentStep.validator ?? z.object({})) as z.ZodType<FieldValues, FieldValues>;
  const trackingOptions = useMemo(
    () => ({
      flowIndex: flowCurrentStep?.name === name ? flowCurrentStep.index : 0,
      registry: { legalEntityRelation }
    }),
    [flowCurrentStep?.name, flowCurrentStep?.index, name, legalEntityRelation]
  );

  useLoadingCleaner();

  const form = useForm({
    resolver: zodResolver(resolvedValidator),
    mode: 'onTouched',
    defaultValues: defaultValuesForValidator(resolvedValidator)
  });

  useEffect(() => {
    if (previousFlowListRef.current !== currentFlowList) {
      form.setValue('bankAccountDocument', bankAccountDocument);
      form.setValue('personType', personType);
    }
  }, [currentFlowList, form, bankAccountDocument, personType]);

  const {
    formState: { errors }
  } = form;

  useEffect(() => {
    scrollToFirstFieldError(Object.keys(errors), name => form.setFocus(name));
  }, [errors, form]);

  useEffect(() => {
    if (!pendingFieldErrors) {
      return;
    }

    const shape = extractShape(resolvedValidator);
    const applicable = Object.entries(pendingFieldErrors).filter(([key]) => shape && key in shape);

    if (!applicable.length) {
      return;
    }

    for (const [name, message] of applicable) {
      form.setValue(name, '');
      form.setError(name, { type: 'server', message });
    }

    scrollToFirstFieldError(
      applicable.map(([name]) => name),
      name => form.setFocus(name)
    );

    clearPendingFieldErrors();
  }, [pendingFieldErrors, clearPendingFieldErrors, resolvedValidator, form]);

  const syncMapper = useCallback(
    (values: FieldValues): FieldValues => {
      const { validator } = currentStep;

      if (!validator) {
        return {};
      }

      const parsed = validator.safeParse(values);

      if (!parsed.success) {
        return {};
      }

      return parsed.data as FieldValues;
    },
    [currentStep]
  );

  useZustandSync<FieldValues>(form.control, syncMapper);

  const persistStep = useCallback(async () => {
    if (untraceable || !isBackendStep(name)) {
      return;
    }

    await saveStep.mutateAsync({
      step: name,
      data: registryStore.getState().registry as unknown as Record<string, unknown>
    });
  }, [name, untraceable, saveStep]);

  const next = useCallback(
    async (action?: () => Promise<unknown>, loading?: LoadingOptions) => {
      if (nextLocked.current) {
        return;
      }

      nextLocked.current = true;
      const isValid = await form.trigger();

      nextLocked.current = false;

      if (!isValid) {
        trackFieldErrors(
          form.formState.errors,
          form.getValues(),
          name,
          currentFlowList,
          flowlistHistory,
          trackingOptions
        );
        return;
      }

      registryStore.getState().setFields(syncMapper(form.getValues()));

      const runActionAndPersist = async () => {
        if (action) {
          await action();
        }

        await persistStep();
      };

      if (loading) {
        triggerLoading({ ...loading, action: runActionAndPersist });
        return;
      }

      try {
        await runActionAndPersist();
      } catch (error) {
        message.error('Não foi possível salvar esta etapa. Verifique sua conexão e tente novamente.');
        throw error;
      }

      trackStepComplete(name, currentFlowList, flowlistHistory, trackingOptions);
      nextStep();
    },
    [form, nextStep, persistStep, triggerLoading, syncMapper, name, currentFlowList, flowlistHistory, trackingOptions]
  );

  const previous = useCallback(() => {
    previousStep();
  }, [previousStep]);

  return (
    <StepContext.Provider value={{ next, previous }}>
      <FormProvider {...form}>
        <StepScopeProvider prefix={scopePrefix}>
          <section className='flex flex-1 flex-col'>
            <Suspense fallback={<Skeleton active />}>
              <currentStep.component />
            </Suspense>
          </section>
        </StepScopeProvider>
      </FormProvider>
    </StepContext.Provider>
  );
};

Step.Label = Label;
Step.Title = Title;
Step.Subtitle = Subtitle;
Step.Content = Content;
Step.Footer = Footer;
